<template>
    <fetch-embd-item :id="itemId">
        <template slot-scope="{response, loading}">
            <div v-if="loading">Loading...</div>
            <div v-else-if="Object.keys(response).length > 0">
                <div v-if="showModalPD">
                    <transition name="modal">
                        <div class="modal-mask">
                            <div class="modal-wrapper">
                                <div class="modal-dialog modal-small-viewer">
                                    <div class="modal-content modal-small-viewer">
                                        <div class="modal-header">
                                            <button type="button" class="close" @click="showModalPD=false">
                                                <span aria-hidden="true">&times;</span>
                                            </button>
                                            <h4 class="modal-title">Primary Data</h4>
                                        </div>
                                        <div class="modal-body" style="font-size: 13px;">
                                            The movies shown in this panel represent only the embryos that were included in automated scoring. Embryos were excluded for scoring for two possible reasons: (1) the data did not pass through quality control checks (embryos cut-off, lost focal plane, inappropriately oriented, or obscured), or (2) embryos were wild-type, when at least 2 embryos from the same condition exhibited defects. In these cases of incomplete penetrance, we exclude wild-type embryos to prevent the loss of low prevalence phenotypes. In such cases, averaging wild-type-like data with arrested data would skew the position of the RNAi condition such that comparisons of observed phenotypes would be difficult to draw. However, if all embryos for a given condition are WT-like, or only one embryo exhibits a non-WT-like phenotype, all embryos are included in the aggregated RNAi profile and all collected movies are shown.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
                <div v-if="showModalAD">
                    <transition name="modal">
                        <div class="modal-mask">
                            <div class="modal-wrapper">
                                <div class="modal-dialog modal-small-viewer">
                                    <div class="modal-content modal-small-viewer">
                                        <div class="modal-header">
                                            <button type="button" class="close" @click="showModalAD=false">
                                                <span aria-hidden="true">&times;</span>
                                            </button>
                                            <h4 class="modal-title">Automated Description</h4>
                                        </div>
                                        <div class="modal-body" style="font-size: 13px;">
                                            The automated description is a best guess of the query gene phenotype based on the proximity of the query gene to the centroid of labelled benchmarked groups in multi-dimensional space. To generate this description, we calculate the <a @click="showModalAD = false; showModalPAD = true;">PAD</a> between the query gene and the midpoint of each of the benchmark datasets, locate the nearest neighbor group (or groups, if very close in value) and apply the description for that group or groups.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
                <div v-if="showModalCSI">
                    <transition name="modal">
                        <div class="modal-mask">
                            <div class="modal-wrapper">
                                <div class="modal-dialog modal-small-viewer">
                                    <div class="modal-content modal-small-viewer">
                                        <div class="modal-header">
                                            <button type="button" class="close" @click="showModalCSI=false">
                                                <span aria-hidden="true">&times;</span>
                                            </button>
                                            <h4 class="modal-title">Connection Specificity Index (CSI)</h4>
                                        </div>
                                        <div class="modal-body" style="font-size: 13px;">
                                            The Connection Specificity Index (CSI) is a graph-theory based metric that evaluates the similarity and specificity of each pair of phenotypes by measuring the number of other genes in the dataset that have knockdown phenotypes that are as or more similar to the phenotypes of either query gene (as assessed by the Pearson’s Correlation Coefficient) than the phenotypes of the queried genes are to each other. Thus, a CSI of 0.99 means that the phenotypes of the two query genes are more similar to each other than they are to the phenotypes for 99% of the genes in the dataset (<router-link to="/embdProjectDescription">Green et al., 2011</router-link>).
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
                <div v-if="showModalPAD">
                    <transition name="modal">
                        <div class="modal-mask">
                            <div class="modal-wrapper">
                                <div class="modal-dialog modal-small-viewer">
                                    <div class="modal-content modal-small-viewer">
                                        <div class="modal-header">
                                            <button type="button" class="close" @click="showModalPAD=false">
                                                <span aria-hidden="true">&times;</span>
                                            </button>
                                            <h4 class="modal-title">Phenotypic Angle of Deviation (PAD)</h4>
                                        </div>
                                        <div class="modal-body" style="font-size: 13px;">
                                            The Phenotypic Angle of Deviation (PAD) compensates for the increase in stochastic variation that is observed as phenotypes become more severe. This metric combines the power of similarity metrics, which captures the angle of spread, and distance metrics, which precisely measure the distance between two compared conditions in multidimensional space. To generate this metric, we normalize the distance between two phenotypes by dividing the Euclidean distance between the considered gene-pair by the sum of the distance to WT for the two vectors. We normalize this on a 0 to 1 scale for simplicity- where values approaching 1 are phenotypically the most similar and values approaching 0 are dissimilar. This transforms the Euclidean Distance measures into PAD values.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
                <div v-if="showModalRec">
                    <transition name="modal">
                        <div class="modal-mask">
                            <div class="modal-wrapper">
                                <div class="modal-dialog modal-small-viewer">
                                    <div class="modal-content modal-small-viewer">
                                        <div class="modal-header">
                                            <button type="button" class="close" @click="showModalRec=false">
                                                <span aria-hidden="true">&times;</span>
                                            </button>
                                            <h4 class="modal-title">Reciprocal Matches</h4>
                                        </div>
                                        <div class="modal-body" style="font-size: 13px;">
                                            Reciprocal phenotypic matches represent high-confidence phenotypic matches where the query gene and the 'hit' are in each others top 10 phenotypic matches, based on rank-ordered <a @click="showModalRec = false; showModalCSI = true;">CSI</a> (Germline or Early Embryo Screen) or <a @click="showModalRec = false; showModalPAD = true;">PAD</a> (Embryonic Development Screen) values.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
                <div v-if="showModalMov">
                    <transition name="modal">
                        <div class="modal-mask">
                            <div class="modal-wrapper">
                                <div class="modal-dialog modal-small-viewer">
                                    <div class="modal-content modal-small-viewer">
                                        <div class="modal-header">
                                            <button type="button" class="close" @click="showModalMov=false">
                                                <span aria-hidden="true">&times;</span>
                                            </button>
                                            <h4 class="modal-title">Top Matches</h4>
                                        </div>
                                        <div class="modal-body" style="font-size: 13px;">
                                            Top phenotypic matches represent the most phenotypically similar conditions, based on scored features. Top phenotypic matches are determined by rank-ordering the genes based on <a @click="showModalMov = false; showModalCSI = true;">CSI</a> (Germline or Early Embryo Screen) or <a @click="showModalMov = false; showModalPAD = true;">PAD</a> (Embryonic Development Screen).
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>


                <!--{{response}}-->
                <h1 class="round">Embryonic Development- Movie Data and Phenotypic Analysis</h1>
                <div class="mainContent">
                    <div class="row">
                      <div class="col-sm-3">
                        <h2 class="card-title">{{response.gene_name}}</h2>
                        <p class="round-description">
                          <b>Other Names:</b> {{response.other_name}}<br/>
                          <b>WormBase ID:</b> <a :href="'https://wormbase.org/species/c_elegans/gene/' + response.wbgene_id" target="_blank">{{response.wbgene_id}}</a><br/>
                        </p>
                      </div>
                      <div class="col-sm-9">
                        <p>{{response.description}}</p>
                      </div>
                    </div>

                    <div class="row">
                        <div class="col-sm-5">
                            <h4 class="round">{{response.gene_name}}(RNAi) Phenotypic Data</h4>

                            <div class="panel panel-default">
                                <div class="panel-heading">
                                    <b>EMBD{{zeroPad(response.embd_id, 4)}}</b>
                                </div>
                                <div class="panel-body">
                                    <table>
                                        <tr>
                                            <td class="body">
                                                <div class="medium">
                                                    <label>Automated description <a @click="showModalAD = true">(explain)</a>: </label> {{response.auto_description}}
                                                </div>
                                            </td>
                                            <td class="container" rowspan="3" style="padding: 0 11px">
                                                <span class="title">Primary data <a @click="showModalPD = true">(explain)</a>:</span>
                                                <video controls loop autoplay width="250" :src="getCompMovie(response.embd_id)">
                                                    Sorry, your browser doesn't support embedded videos.
                                                </video>
                                                <a :href="'/movieView/' + response.gene_name + '/' + response.embd_id">
                                                    <div class="panel-title blue">Link to Time Aligned Movies</div>
                                                </a>
                                                <a href="/mov/Development_controls.mp4">
                                                    <p class="panel-title blue">Control Reference Movie (link)</p>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="container">
                                                <img :src="getGLSUrl(response.embd_id)" width="400"/>
                                                <a :href="'/figureView/' + response.embd_id">
                                                    <div class="panel-title blue">Link to Feature Curves</div>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="container linkout">
                                                <a :href="'/manualAnno/' + itemId">
                                                    <div class="centered blue">Link to Manual Annotation</div>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="body" colspan="2">
                                                T3 Primer : <input type="text" :value="response.oligo_1">
                                                <button class="copy-button" :name="response.oligo_1" :id="response.oligo_1" :data-clipboard-text="response.oligo_1">Copy</button>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="body" colspan="2">
                                                T7 Primer : <input type="text" :value="response.oligo_2">
                                                <button class="copy-button" :name="response.oligo_2" :id="response.oligo_2" :data-clipboard-text="response.oligo_2">Copy</button>
                                            </td>
                                        </tr>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div class="col-sm-7">
                            <h4 class="round">Best Phenotypic Matches for {{response.gene_name}}(RNAi)</h4>
                          Best matches are shown for the optimal parameter set derived from both strains. To see best matches for the strain-specific parameter sets, click <a :href="'/umapView/' + response.gene_name + '/' + response.embd_id" class="underline">here</a>.
                            <div class="panel panel-default">
                                <div class="panel-heading">
                                    <div class="row">
                                        <FetchRank :id="response.embd_id" class="col-sm-8">
                                            <template slot-scope="{response, loading}">
                                                <div v-if="loading">Loading...</div>
                                                <div v-else-if="response.length > 0">
                                                    <table class="bestPheno table table-striped table-hover table-responsive">
                                                        <thead>
                                                        <tr class="active">
                                                            <th>Gene</th>
                                                            <th>PAD <a @click="showModalPAD = true">(explain)</a></th>
                                                            <th>Description</th>
                                                            <th>Reciprocal Match</th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        <tr v-for="(item, index) in response" v-bind:key="index" v-bind:class="{active: index < 5}">
                                                            <th scope="row" class="gene-name"><a :href="'/embdView/' + item.target_embd_id">{{item.gene_name}}</a></th>
                                                            <td>{{item.pad}}</td>
                                                            <td>{{shorten(item.description)}}</td>
                                                            <td><i class="fa fa-check" aria-hidden="true" style="color: royalblue;" v-show="item.reciprocal_hit === 1"></i>
                                                                <i class="fa fa-close" aria-hidden="true" style="color: crimson;" v-show="item.reciprocal_hit !== 1"></i>
                                                                {{item.reciprocal_hit === 1}}</td>
                                                        </tr>
                                                        </tbody>
                                                    </table>
                                                    <div v-show="response.length > 5">
                                                        <a v-on:click="showMore('bestPheno', response.length)" v-show="!shownMore.bestPheno">Show more</a>
                                                        <a v-on:click="showLess('bestPheno')" v-show="shownMore.bestPheno">Show less</a>
                                                    </div>
                                                </div>
                                            </template>
                                        </FetchRank>
                                        <div class="container col-sm-4">
                                            <div class="round">Reciprocal Phenotypic Matches <a @click="showModalRec = true">(explain)</a></div>

                                            <video id="video" controls loop autoplay width="250" :src="getReciprocalHitsMovie(response.embd_id)" onerror="$('#video').hide();$('#alt-vid').show();">
                                                Sorry, your browser doesn't support embedded videos.
                                            </video>
                                            <p class="panel-body red" id="alt-vid" style="display: none">No Reciprocal Matches, see Top-Hit Movies (below)</p>

                                            <p class="panel-body blue">
                                                <a class="blue" href="/mov/Development_controls.mp4">
                                                Control Reference Movie (link)
                                                </a>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="panel-title blue">
                                <a :href="'/movieView/' + response.gene_name + '/' + response.embd_id">
                                    Link to top-hit movies </a>
                                <a @click="showModalMov = true;">
                                    (explain)
                                </a>
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-sm-5">
                            <!--<h4 class="round">Clique (Placeholder)</h4>-->
                        </div>
                        <div class="col-sm-7">
                            <h4 class="round">UMAP-optimal parameters (from both strains) <a :href="'/umapView/' + response.gene_name + '/' + response.embd_id" class="underline" style="float: right; font-size: 16px; padding-top: 4px;">Link to strain specific UMAPs</a></h4>
                            <div class="panel panel-default">
                                <img :src="getUmapPADOptSvg(response.embd_id)"/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else>
                No record
            </div>
        </template>
    </fetch-embd-item>
</template>

<script>
  import FetchEmbdItem from './FetchEmbdItem.vue'
  import FetchRank from "./FetchRank";
  const $ = window.jQuery = require('jquery');
  require('../js/clipboard')

  export default {
    name: "ItemView.vue",
    components: {
      FetchRank,
      FetchEmbdItem,
    },
    data() {
      return {
        shownMore: {
          bestPheno: false,
        },
        itemId: this.$route.params.id,
        publicPath: process.env.BASE_URL,
        showModalPD: false,
        showModalAD: false,
        showModalCSI: false,
        showModalPAD: false,
        showModalRec: false,
        showModalMov: false,
      }
    },
    methods: {
      shorten(desc) {
        if(desc.length > 100) {
          return desc.substr(0, 100) + '...'
        } else {
          return desc.substr(0, 100)
        }
      },
      showLess(clazz) {
        let $rows = $('table.' + clazz + ' tr')
        $rows.filter(':gt(' + (5) + ')').removeClass('active')
        this.shownMore[clazz] = false
      },
      showMore(clazz, len) {
        let $rows = $('table.' + clazz + ' tr')
        let lastActiveIndex = $rows.filter('.active:last').index()
        $rows.filter(':lt(' + (lastActiveIndex + len) + ')').addClass('active')
        this.shownMore[clazz] = true
      },
      zeroPad(num, places) {
        var zero = places - num.toString().length + 1;
        return Array(+(zero > 0 && zero)).join("0") + num;
      },
      getGLSUrl(num) {
        const vm = this
        let id = vm.zeroPad(num, 4)
        return `/svg/EMBD${id}_all_GLS.svg`
      },
      getMSUrl(num) {
        const vm = this
        let id = vm.zeroPad(num, 4)
        return `/svg/EMBD${id}_all_MS.svg`
      },
      getCompMovie(num) {
        const vm = this
        let id = vm.zeroPad(num, 4)
        return `/mov/EMBD${id}_Comp.mp4`
      },
      getTophitsMovie(num) {
        const vm = this
        let id = vm.zeroPad(num, 4)
        return `/mov/EMBD${id}_tophits.mp4`
      },
      getReciprocalHitsMovie(num) {
        const vm = this
        let id = vm.zeroPad(num, 4)
        return `/mov/EMBD${id}_reciprocal_hits.mp4`
      },
      getTsnePADSvg(num) {
        const vm = this
        let id = vm.zeroPad(num, 4)
        return `/svg/EMBD${id}_tsne_PAD.svg`
      },
      getUmapPADOptSvg(num) {
        const vm = this
        let id = vm.zeroPad(num, 4)
        return `/svg/EMBD${id}_UMAP_PAD_optParams.svg`
      }
    },
    mounted() {
      // const vm = this
      // /* eslint-disable no-console */
      // console.log(vm.itemId);
    },
  }
</script>

<style>
    @import url('~@/assets/bootstrap.css');
    @import url('~@/assets/datatable.css');
    @import url('~@/assets/tooltip.css');

    table.bestPheno tr { display: none; }
    table.bestPheno tr.active { display: table-row; }

    th.gene-name {
        white-space: nowrap
    }

    .gray {
        background-color: #f5f5f5;
    }

    .medium > label {
        text-decoration: underline;
    }

    .medium {
        font-weight: normal;
    }

    .container {
        position: relative;
        text-align: center;
        color: white;
    }

    span.title {
        color: black;
        font-size: 12px;
    }

    .body {
        font-weight: bold;
        font-size: 15px;
    }

    .centered {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-weight: bold;
        font-size: 20px;
    }

    .underline {
      text-decoration: underline;
    }

    .blue {
        color: #2834ff;
    }

    .red {
        color: #ff1a23;
    }

    .linkout {
        height: 150px;
    }

    .white {
        color: white;
    }

    .round {
        font-family: Calibri, Candara, Segoe, Segoe UI, Optima, Arial;
        border-radius: 7px;
        border: 1px solid #d9d9d9;
        background: #dae3f3;
        padding: 3px 7px 3px 7px;
        font-style: oblique;
        color: #203864;
        font-weight: bold;
    }

    .round-description {
      font-family: Calibri, Candara, Segoe, Segoe UI, Optima, Arial;
      border-radius: 7px;
      background: #dae3f3;
      padding: 3px 7px 3px 7px;
    }

    p.strong {
        text-decoration: underline;
        font-style: oblique;
    }

    h2.card-title {
        margin-top: 0;
        color: #2f5597;
        font-style: oblique;
    }

    h5.card-title {
        color: #2f5597;
        font-style: oblique;
        font-weight: bold;
    }

    .mainContent {
        border-radius: 25px;
        border: 2px solid #dae3f3;
        padding: 20px;
    }

    .modal-title {
        color: #114188;
        font-weight: bold;
        font-size: 18px;
    }

    .modal-mask {
        position: fixed;
        z-index: 9998;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, .5);
        display: table;
        transition: opacity .3s ease;
    }

    .modal-wrapper {
        display: table-cell;
        vertical-align: middle;
    }

    .modal-small-viewer {
        width: 600px;
    }
</style>